/**
 * Auth Reducer
 * 
 * Manages authentication state including login, signup, 2FA, magic link,
 * and passkey flows with comprehensive validation and error handling.
 */

import type { AuthState, AuthAction } from "@/types/state";

/**
 * Get initial auth state with restoration from sessionStorage
 */
export const getInitialAuthState = (): AuthState => {
  const AUTH_STATE_KEY = 'auth_flow_state';
  const MAX_STATE_AGE_MS = 15 * 60 * 1000; // 15 minutes
  
  try {
    const storedState = sessionStorage.getItem(AUTH_STATE_KEY);
    if (storedState) {
      const parsed = JSON.parse(storedState);
      const age = Date.now() - parsed.timestamp;
      
      if (age < MAX_STATE_AGE_MS) {
        console.log('[authReducer] Restoring auth state from sessionStorage:', {
          step: parsed.step,
          email: parsed.email,
          age: `${Math.round(age / 1000)}s ago`
        });
        
        return {
          ...initialAuthState,
          step: parsed.step || 'idle',
          mode: parsed.mode || 'login',
          loginStep: parsed.loginStep || 'email',
          signupStep: parsed.signupStep || 'email',
          email: parsed.email || '',
          selectedMethod1: parsed.selectedMethod1 || null,
          selectedMethod2: parsed.selectedMethod2 || null,
          availableMethods: parsed.availableMethods || [],
          authProgress: parsed.authProgress || initialAuthState.authProgress,
          rememberDevice: parsed.rememberDevice || false,
          emailValid: !!parsed.email, // If we have an email, mark it as valid
        };
      } else {
        console.log('[authReducer] Persisted state too old, clearing');
        sessionStorage.removeItem(AUTH_STATE_KEY);
      }
    }
  } catch (error) {
    console.error('[authReducer] Failed to restore auth state:', error);
    sessionStorage.removeItem(AUTH_STATE_KEY);
  }
  
  return initialAuthState;
};

/**
 * Initial authentication state
 */
export const initialAuthState: AuthState = {
  // Unified FSM step
  step: 'idle',
  
  // Mode and flow
  mode: 'login',
  loginStep: 'email',
  signupStep: 'email',
  
  // Progressive auth tracking
  selectedMethod1: null,
  selectedMethod2: null,
  availableMethods: [],
  authProgress: {
    emailVerified: false,
    method1Completed: false,
    method2Completed: false,
  },
  
  // Form values
  email: '',
  password: '',
  totpCode: '',
  resetEmail: '',
  communicationCode: '',
  
  // Loading states
  loading: false,
  backgroundChecking: false,
  checkingPasskeySupport: true,
  
  // UI states
  showPassword: false,
  showPasswordSkeleton: false,
  forgotPasswordOpen: false,
  show2FAVerification: false,
  
  // Validation states
  emailError: null,
  emailValid: false,
  emailTouched: false,
  passwordError: null,
  passwordStrength: null,
  passwordTouched: false,
  
  // Magic link states
  magicLinkSent: false,
  resendCooldown: 0,
  magicLinkCountdown: 900, // 15 minutes
  
  // Auth states
  tfaRequired: false,
  pendingUserId: null,
  deviceFingerprint: '',
  passkeySupported: false,
  rememberDevice: false,
  
  // Accessibility
  authStateMessage: '',
};

/**
 * Auth reducer
 * Handles all state updates for authentication flows
 */
export function authReducer(
  state: AuthState,
  action: AuthAction
): AuthState {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, step: action.payload };

    case 'SET_MODE':
      return { ...state, mode: action.payload };

    case 'SET_LOGIN_STEP':
      return { ...state, loginStep: action.payload };

    case 'SET_SIGNUP_STEP':
      return { ...state, signupStep: action.payload };

    case 'SET_SELECTED_METHOD_1':
      return { ...state, selectedMethod1: action.payload };
    
    case 'SET_SELECTED_METHOD_2':
      return { ...state, selectedMethod2: action.payload };
    
    case 'SET_AVAILABLE_METHODS':
      return { ...state, availableMethods: action.payload };
    
    case 'UPDATE_AUTH_PROGRESS':
      return {
        ...state,
        authProgress: { ...state.authProgress, ...action.payload },
      };

    case 'SET_EMAIL':
      return { ...state, email: action.payload };

    case 'SET_PASSWORD':
      return { ...state, password: action.payload };

    case 'SET_TOTP_CODE':
      return { ...state, totpCode: action.payload };

    case 'SET_RESET_EMAIL':
      return { ...state, resetEmail: action.payload };

    case 'SET_COMMUNICATION_CODE':
      return { ...state, communicationCode: action.payload };

    case 'SET_LOADING':
      return { ...state, loading: action.payload };

    case 'SET_BACKGROUND_CHECKING':
      return { ...state, backgroundChecking: action.payload };

    case 'SET_CHECKING_PASSKEY_SUPPORT':
      return { ...state, checkingPasskeySupport: action.payload };

    case 'TOGGLE_SHOW_PASSWORD':
      return { ...state, showPassword: !state.showPassword };

    case 'SET_SHOW_PASSWORD_SKELETON':
      return { ...state, showPasswordSkeleton: action.payload };

    case 'SET_FORGOT_PASSWORD_OPEN':
      return { ...state, forgotPasswordOpen: action.payload };

    case 'SET_SHOW_2FA_VERIFICATION':
      return { ...state, show2FAVerification: action.payload };

    case 'SET_EMAIL_VALIDATION':
      return {
        ...state,
        emailError: action.payload.error,
        emailValid: action.payload.valid,
      };

    case 'SET_EMAIL_TOUCHED':
      return { ...state, emailTouched: action.payload };

    case 'SET_PASSWORD_VALIDATION':
      return {
        ...state,
        passwordError: action.payload.error,
        passwordStrength: action.payload.strength,
      };

    case 'SET_PASSWORD_TOUCHED':
      return { ...state, passwordTouched: action.payload };

    case 'SET_MAGIC_LINK_SENT':
      return { ...state, magicLinkSent: action.payload };

    case 'SET_RESEND_COOLDOWN':
      return { ...state, resendCooldown: action.payload };

    case 'DECREMENT_RESEND_COOLDOWN':
      return {
        ...state,
        resendCooldown: Math.max(0, state.resendCooldown - 1),
      };

    case 'SET_MAGIC_LINK_COUNTDOWN':
      return { ...state, magicLinkCountdown: action.payload };

    case 'DECREMENT_MAGIC_LINK_COUNTDOWN':
      return {
        ...state,
        magicLinkCountdown: Math.max(0, state.magicLinkCountdown - 1),
      };

    case 'SET_TFA_REQUIRED':
      return { ...state, tfaRequired: action.payload };

    case 'SET_PENDING_USER_ID':
      return { ...state, pendingUserId: action.payload };

    case 'SET_DEVICE_FINGERPRINT':
      return { ...state, deviceFingerprint: action.payload };

    case 'SET_PASSKEY_SUPPORTED':
      return { ...state, passkeySupported: action.payload };

    case 'SET_REMEMBER_DEVICE':
      return { ...state, rememberDevice: action.payload };

    case 'SET_AUTH_STATE_MESSAGE':
      return { ...state, authStateMessage: action.payload };

    case 'CLEAR_SENSITIVE_DATA':
      return {
        ...state,
        password: '',
        totpCode: '',
      };

    case 'RESET_TO_EMAIL_STEP':
      return {
        ...state,
        step: 'promptingEmail',
        loginStep: 'email',
        signupStep: 'email',
        email: '',
        password: '',
        totpCode: '',
        selectedMethod1: null,
        selectedMethod2: null,
        availableMethods: [],
        tfaRequired: false,
        backgroundChecking: false,
        showPasswordSkeleton: false,
        emailValid: false,
        emailTouched: false,
        authProgress: {
          emailVerified: false,
          method1Completed: false,
          method2Completed: false,
        },
      };

    case 'RESET_AUTH':
      return initialAuthState;

    default:
      return state;
  }
}

/**
 * Action creators for cleaner dispatch calls
 */
export const authActions = {
  setStep: (step: AuthState['step']): AuthAction => ({
    type: 'SET_STEP',
    payload: step,
  }),
  
  setMode: (mode: AuthState['mode']): AuthAction => ({
    type: 'SET_MODE',
    payload: mode,
  }),
  
  setLoginStep: (step: AuthState['loginStep']): AuthAction => ({
    type: 'SET_LOGIN_STEP',
    payload: step,
  }),
  
  setSignupStep: (step: AuthState['signupStep']): AuthAction => ({
    type: 'SET_SIGNUP_STEP',
    payload: step,
  }),
  
  setSelectedMethod1: (method: AuthState['selectedMethod1']): AuthAction => ({
    type: 'SET_SELECTED_METHOD_1',
    payload: method,
  }),
  
  setSelectedMethod2: (method: AuthState['selectedMethod2']): AuthAction => ({
    type: 'SET_SELECTED_METHOD_2',
    payload: method,
  }),
  
  setAvailableMethods: (methods: AuthState['availableMethods']): AuthAction => ({
    type: 'SET_AVAILABLE_METHODS',
    payload: methods,
  }),
  
  updateAuthProgress: (progress: Partial<AuthState['authProgress']>): AuthAction => ({
    type: 'UPDATE_AUTH_PROGRESS',
    payload: progress,
  }),
  
  setEmail: (email: string): AuthAction => ({
    type: 'SET_EMAIL',
    payload: email,
  }),
  
  setPassword: (password: string): AuthAction => ({
    type: 'SET_PASSWORD',
    payload: password,
  }),
  
  setTotpCode: (code: string): AuthAction => ({
    type: 'SET_TOTP_CODE',
    payload: code,
  }),
  
  setResetEmail: (email: string): AuthAction => ({
    type: 'SET_RESET_EMAIL',
    payload: email,
  }),
  
  setCommunicationCode: (code: string): AuthAction => ({
    type: 'SET_COMMUNICATION_CODE',
    payload: code,
  }),
  
  setLoading: (loading: boolean): AuthAction => ({
    type: 'SET_LOADING',
    payload: loading,
  }),
  
  setBackgroundChecking: (checking: boolean): AuthAction => ({
    type: 'SET_BACKGROUND_CHECKING',
    payload: checking,
  }),
  
  setCheckingPasskeySupport: (checking: boolean): AuthAction => ({
    type: 'SET_CHECKING_PASSKEY_SUPPORT',
    payload: checking,
  }),
  
  toggleShowPassword: (): AuthAction => ({
    type: 'TOGGLE_SHOW_PASSWORD',
  }),
  
  setShowPasswordSkeleton: (show: boolean): AuthAction => ({
    type: 'SET_SHOW_PASSWORD_SKELETON',
    payload: show,
  }),
  
  setForgotPasswordOpen: (open: boolean): AuthAction => ({
    type: 'SET_FORGOT_PASSWORD_OPEN',
    payload: open,
  }),
  
  setShow2FAVerification: (show: boolean): AuthAction => ({
    type: 'SET_SHOW_2FA_VERIFICATION',
    payload: show,
  }),
  
  setEmailValidation: (error: string | null, valid: boolean): AuthAction => ({
    type: 'SET_EMAIL_VALIDATION',
    payload: { error, valid },
  }),
  
  setEmailTouched: (touched: boolean): AuthAction => ({
    type: 'SET_EMAIL_TOUCHED',
    payload: touched,
  }),
  
  setPasswordValidation: (error: string | null, strength: AuthState['passwordStrength']): AuthAction => ({
    type: 'SET_PASSWORD_VALIDATION',
    payload: { error, strength },
  }),
  
  setPasswordTouched: (touched: boolean): AuthAction => ({
    type: 'SET_PASSWORD_TOUCHED',
    payload: touched,
  }),
  
  setMagicLinkSent: (sent: boolean): AuthAction => ({
    type: 'SET_MAGIC_LINK_SENT',
    payload: sent,
  }),
  
  setResendCooldown: (seconds: number): AuthAction => ({
    type: 'SET_RESEND_COOLDOWN',
    payload: seconds,
  }),
  
  decrementResendCooldown: (): AuthAction => ({
    type: 'DECREMENT_RESEND_COOLDOWN',
  }),
  
  setMagicLinkCountdown: (seconds: number): AuthAction => ({
    type: 'SET_MAGIC_LINK_COUNTDOWN',
    payload: seconds,
  }),
  
  decrementMagicLinkCountdown: (): AuthAction => ({
    type: 'DECREMENT_MAGIC_LINK_COUNTDOWN',
  }),
  
  setTfaRequired: (required: boolean): AuthAction => ({
    type: 'SET_TFA_REQUIRED',
    payload: required,
  }),
  
  setPendingUserId: (userId: string | null): AuthAction => ({
    type: 'SET_PENDING_USER_ID',
    payload: userId,
  }),
  
  setDeviceFingerprint: (fingerprint: string): AuthAction => ({
    type: 'SET_DEVICE_FINGERPRINT',
    payload: fingerprint,
  }),
  
  setPasskeySupported: (supported: boolean): AuthAction => ({
    type: 'SET_PASSKEY_SUPPORTED',
    payload: supported,
  }),
  
  setRememberDevice: (remember: boolean): AuthAction => ({
    type: 'SET_REMEMBER_DEVICE',
    payload: remember,
  }),
  
  setAuthStateMessage: (message: string): AuthAction => ({
    type: 'SET_AUTH_STATE_MESSAGE',
    payload: message,
  }),
  
  clearSensitiveData: (): AuthAction => ({
    type: 'CLEAR_SENSITIVE_DATA',
  }),
  
  resetToEmailStep: (): AuthAction => ({
    type: 'RESET_TO_EMAIL_STEP',
  }),
  
  resetAuth: (): AuthAction => ({
    type: 'RESET_AUTH',
  }),
};
